import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogBackdrop, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { Button, Select, SelectItem, Text, Title } from "@tremor/react";
import * as sourcesAPI from "../../services/sources";
import { getEndpoint } from "../sources/utils";
import { useNavigate } from "react-router-dom";

function formatRelative(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return d.toLocaleString();
}

function truncateHex(hex, max = 12) {
  if (!hex) return "";
  if (hex.length <= max) return hex;
  return `${hex.slice(0, max)}…`;
}

function NoSourcesEmptyState({ onCreate }) {
  return (
    <div className="text-center py-8">
      <Text className="mb-3">
        You don't have any sources yet. Sources receive events from your network server.
      </Text>
      <Button variant="primary" size="xs" onClick={onCreate}>
        Create a source
      </Button>
    </div>
  );
}

function NoEventsEmptyState({ source }) {
  const endpoint = source ? getEndpoint(source) : "";
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (!endpoint) return;
    await navigator.clipboard.writeText(endpoint);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="text-center py-6">
      <Text className="mb-3">
        No events received yet. Make sure your sensor is online and pointing at this source's ingest URL.
      </Text>
      {endpoint && (
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <ClipboardDocumentIcon className="h-4 w-4" />
          {copied ? "Copied!" : "Copy ingest URL"}
        </button>
      )}
    </div>
  );
}

/**
 * Recent events drawer — lets the user pick a real raw payload from one of
 * their sources to drop into the Test tab inputs.
 *
 * Self-contained: fetches the user's sources on open, fetches events when a
 * source is selected. On row click calls onPick({hex, fPort}) and closes.
 */
export default function RecentEventsDrawer({ isOpen, onClose, onPick }) {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);

  // Fetch sources when the drawer opens.
  useEffect(() => {
    if (!isOpen) return;
    setSourcesLoaded(false);
    sourcesAPI.list(1, 100).then((resp) => {
      const items = resp?.results || resp?.data || resp || [];
      setSources(items);
      setSourcesLoaded(true);
      // Auto-select if exactly one source.
      if (items.length === 1) {
        setSelectedId(items[0]._id);
      } else {
        setSelectedId("");
      }
    }).catch(() => {
      setSources([]);
      setSourcesLoaded(true);
    });
  }, [isOpen]);

  // Fetch events whenever the selected source changes.
  useEffect(() => {
    if (!isOpen || !selectedId) {
      setEvents([]);
      setEventsLoaded(false);
      return;
    }
    setEventsLoaded(false);
    sourcesAPI.listEvents(selectedId).then((resp) => {
      setEvents(resp?.events || []);
      setEventsLoaded(true);
    }).catch(() => {
      setEvents([]);
      setEventsLoaded(true);
    });
  }, [isOpen, selectedId]);

  const selectedSource = useMemo(
    () => sources.find((s) => s._id === selectedId) || null,
    [sources, selectedId]
  );

  const onRowClick = (evt) => {
    if (!evt.hex) return; // unparseable rows aren't actionable
    onPick({ hex: evt.hex, fPort: evt.fPort != null ? evt.fPort : 1 });
    onClose();
  };

  return (
    <Transition show={isOpen}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          enter="transition duration-200 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-150 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-black/30" />
        </TransitionChild>
        <div className="fixed inset-0 flex justify-end">
          <TransitionChild
            enter="transition duration-200 ease-out"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition duration-150 ease-in"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="w-full max-w-md bg-white dark:bg-gray-900 shadow-xl flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-800">
                <Title>Recent events</Title>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="px-4 py-3 flex-1 overflow-y-auto">
                {!sourcesLoaded && (
                  <Text className="text-sm">Loading sources…</Text>
                )}

                {sourcesLoaded && sources.length === 0 && (
                  <NoSourcesEmptyState
                    onCreate={() => {
                      onClose();
                      navigate("/sources");
                    }}
                  />
                )}

                {sourcesLoaded && sources.length > 0 && (
                  <>
                    <label className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                      Source
                    </label>
                    <Select
                      value={selectedId}
                      onValueChange={setSelectedId}
                      placeholder="Pick a source"
                      className="mb-3"
                    >
                      {sources.map((s) => (
                        <SelectItem key={s._id} value={s._id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </Select>

                    {selectedId && !eventsLoaded && (
                      <Text className="text-sm">Loading events…</Text>
                    )}

                    {selectedId && eventsLoaded && events.length === 0 && (
                      <NoEventsEmptyState source={selectedSource} />
                    )}

                    {selectedId && eventsLoaded && events.length > 0 && (
                      <>
                        <Text className="text-sm text-tremor-content dark:text-dark-tremor-content mb-2">
                          Last {events.length} events
                        </Text>
                        <ul className="divide-y">
                          {events.map((evt, i) => (
                            <li key={i}>
                              <button
                                type="button"
                                onClick={() => onRowClick(evt)}
                                disabled={!evt.hex}
                                className={`w-full text-left py-2 px-1 text-sm font-mono ${
                                  evt.hex
                                    ? "hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                                    : "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                }`}
                              >
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatRelative(evt.timestamp)}
                                </span>
                                {evt.error === "unparseable" ? (
                                  <span className="ml-2 italic">Unparseable payload</span>
                                ) : (
                                  <>
                                    {evt.fPort != null && (
                                      <span className="ml-2 text-gray-700 dark:text-gray-300">
                                        fPort {evt.fPort}
                                      </span>
                                    )}
                                    <span className="ml-2 text-gray-900 dark:text-gray-50">
                                      {truncateHex(evt.hex)}
                                    </span>
                                  </>
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
